import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listUserAssignments,
  replaceUserAssignments,
  type AccessRole,
  type AssignmentInput,
} from '@/api/auth';
import { listAllEntities } from '@/api/entities';
import { listAllDivisions } from '@/api/divisions';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import Select from '@/components/ui/Select';
import Spinner from '@/components/ui/Spinner';
import IconButton from '@/components/ui/IconButton';
import { Trash2, Plus } from 'lucide-react';
import type { User } from '@/types';

// A draft row: role + one target encoded as "ent:<id>" or "div:<id>".
type DraftRow = { role: AccessRole; target: string };

const roleOptions = [
  { value: 'PREPARER', label: 'Preparer' },
  { value: 'REVIEWER', label: 'Reviewer' },
];

/**
 * Admin modal for a user's entity/division access assignments (MH item 7).
 * A division assignment covers every entity in that division; a user may hold
 * any mix of roles across targets, including both roles on the same target.
 */
export default function AccessAssignmentsModal({
  user,
  onClose,
}: {
  user: User;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<DraftRow[]>([]);
  const [newRole, setNewRole] = useState<AccessRole>('PREPARER');
  const [newTarget, setNewTarget] = useState('');
  const [error, setError] = useState('');

  const { data: assignments, isLoading } = useQuery({
    queryKey: ['user-assignments', user.id],
    queryFn: () => listUserAssignments(user.id),
  });

  const { data: divisions } = useQuery({
    queryKey: ['admin-divisions'],
    queryFn: listAllDivisions,
  });

  const { data: entities } = useQuery({
    queryKey: ['admin-entities'],
    queryFn: listAllEntities,
  });

  // Seed the editable rows from the saved assignments once loaded.
  useEffect(() => {
    if (assignments) {
      setRows(
        assignments.map((a) => ({
          role: a.role,
          target: a.entityId ? `ent:${a.entityId}` : `div:${a.divisionId}`,
        })),
      );
    }
  }, [assignments]);

  const saveMutation = useMutation({
    mutationFn: (inputs: AssignmentInput[]) =>
      replaceUserAssignments(user.id, inputs),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['user-assignments', user.id] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Save failed';
      setError(msg);
    },
  });

  const targetOptions = [
    ...(divisions ?? []).map((d) => ({
      value: `div:${d.id}`,
      label: `Division — ${d.name}`,
    })),
    ...(entities ?? []).map((e) => ({
      value: `ent:${e.id}`,
      label: `${e.name} (${e.code})`,
    })),
  ];

  const targetLabel = (target: string): string =>
    targetOptions.find((o) => o.value === target)?.label ?? target;

  const addRow = () => {
    if (!newTarget) return;
    const exists = rows.some((r) => r.role === newRole && r.target === newTarget);
    if (!exists) setRows((prev) => [...prev, { role: newRole, target: newTarget }]);
    setNewTarget('');
  };

  const save = () => {
    setError('');
    saveMutation.mutate(
      rows.map((r) => ({
        role: r.role,
        ...(r.target.startsWith('ent:')
          ? { entityId: r.target.slice(4) }
          : { divisionId: r.target.slice(4) }),
      })),
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Access — ${user.name ?? user.email}`}
      size="lg"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={saveMutation.isPending} onClick={save}>
            Save
          </Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-gray-700">
        <p>
          Assignments restrict this user to the entities they&apos;ve been given.
          A <strong>division</strong> assignment covers every entity in it
          (including entities added later). A user with{' '}
          <strong>no assignments</strong> keeps full access (legacy behaviour).
          Admin and Group Finance users always have full access regardless.
        </p>

        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner size="md" />
          </div>
        ) : (
          <>
            {rows.length === 0 ? (
              <p className="italic text-gray-500">
                No assignments — this user currently has full access.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                {rows.map((row, idx) => (
                  <li key={`${row.role}:${row.target}`} className="flex items-center justify-between px-3 py-2">
                    <span className="flex items-center gap-2">
                      <Badge variant={row.role === 'PREPARER' ? 'blue' : 'amber'}>
                        {row.role === 'PREPARER' ? 'Preparer' : 'Reviewer'}
                      </Badge>
                      <span>{targetLabel(row.target)}</span>
                    </span>
                    <IconButton
                      icon={Trash2}
                      label={`Remove ${row.role.toLowerCase()} assignment for ${targetLabel(row.target)}`}
                      variant="danger"
                      onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                    />
                  </li>
                ))}
              </ul>
            )}

            {/* Add row */}
            <div className="flex items-end gap-2">
              <div className="w-36">
                <Select
                  label="Role"
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as AccessRole)}
                  options={roleOptions}
                />
              </div>
              <div className="flex-1">
                <Select
                  label="Division or entity"
                  value={newTarget}
                  onChange={(e) => setNewTarget(e.target.value)}
                  options={[{ value: '', label: 'Select…' }, ...targetOptions]}
                />
              </div>
              <Button size="sm" variant="secondary" onClick={addRow} disabled={!newTarget}>
                <Plus className="h-3.5 w-3.5" />
                Add
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
